import { vars } from '../utils/vars';

/**
 * Render Lifecycle:
 *   render()
 *   afterRender()
 *   renderDone event fired
 *   domReady() fired (asynchronously after DOM has loaded)
 * 
 */
export class ComponentBase {
    constructor(data = {}) {
        const defaults = {
            tag: 'div',
            class: '',
            id: '',
            style: '',
        };
        let options = {...defaults, ...data}

        this.el = document.createElement(options.tag);

        if (options.class) {
            this.el.className = options.class;
        }
        if (options.id) {
            this.el.id = options.id;
        }
        if (options.style) {
            this.el.setAttribute('style', options.style);
        }

        this.eventController = new AbortController();

        this.attachEvents();
    }

    setProperty(name, value) {
        this.el.setAttribute(name, value);
    }

    /**
     * Attaches events easily using a registerEvents() method. registerEvents() should return
     * an object with contents in the following format:
     *      'event  selector'  : 'handler'
     * 
     * 'selector' is optional. If omitted, the listener will fire on this.el
     * 
     * If you need to attach events to objects outside this.el (like window or document),
     * use this.addEventListener() below
     */
    attachEvents() {
        if (this.registerEvents) {
            const events = this.registerEvents();
            for (const evtConfig in events) {
                const method = events[evtConfig];
                let parts = evtConfig.trim().replace(/\s{2,}/g, ' ').split(/\s/g);
                const event = parts.shift();
                const selector = parts.join(' ');

                if (selector.trim() != '') {
                    // Event fires on specific elements
                    this.el.addEventListener(event, (e) => {
                        if (e.target.matches(selector) || e.target.closest(selector)) {
                            this[method].call(this, e);
                        }
                    }, {signal: this.eventController.signal });
                } else {
                    // Event fires on this.el
                    this.el.addEventListener(event, (e) => {
                        this[method].call(this, e);
                    }, {signal: this.eventController.signal });
                }
            }
        }
    }

    /**
     * Attaches an event handler to an event. This method is useful when attaching events
     * to elements outside this.el. All events attached with this method are removed
     * on this.destroy()
     * @param {*} el - Element to attach event to. Can be anything that accepts the native addEventListener
     * @param {String} event 
     * @param {Function} handler 
     * @param {Object} options - Options passed into native addEventListener
     */
    addEventListener(el, event, handler, options = {}) {
        el.addEventListener(event, handler, { signal: this.eventController.signal, ...options });
    }

    render(template) {
        if (this.template || template) {
            this.el.innerHTML = '';
            const html = template || this.template();

            // Length of HTMLCollection changes after every append so cache it here before starting
            const length = html.length;
            for (let i = 0; i < length; i++) {
                this.el.append(html[0]);    // Elements are removed from the HTMLCollection after append, so always use 0 index
            }
        }

        if (this.afterRender) {
            this.afterRender();
        }

        this.el.dispatchEvent(new Event('renderDone', { bubbles: true }));

        if (this.domReady) {
            this.observe(this.el, () => {
                this.domReady();
            });
        }
    }

    /**
     * Renders a template into a specific element
     * @param {HTMLCollection} template - Elements to render. Likely returned from tagged template literal "template"
     * @param {Element} targetElement - Element in which to render
     * @param {Object} options - Object of options. Available options:
     *      replace: Boolean - whether to replace contents of targetElement or append to them
     *      renderDone: Function
     *      domReady: Function
     */
    renderInto(template, targetElement, options = {}) {
        options = {
            replace: true,
            ...options
        }

        if (options.replace) {
            targetElement.innerHTML = '';
        }

        // Length of HTMLCollection changes after every append so cache it here before starting
        const length = template.length;
        for (let i = 0; i < length; i++) {
            targetElement.append(template[0]);    // Elements are removed from the HTMLCollection after append, so always use 0 index
        }

        if (options.renderDone) {
            this.observe(targetElement, () => {
                options.renderDone();
            });
        }

        if (options.domReady) {
            this.observe(targetElement, () => {
                options.domReady();
            });
        }
    }

    /**
     * Waits until an element's dom has calculated layout and then fires callback
     * @param {Element} element - Element in which to observe
     * @param {Function} callback - Code to execute
     */
    observe(element, callback) {
        /*
            Per MDN: Implementations should, if they follow the specification, invoke resize events before paint and after layout.
            This means the ResizeObserver should be fired after all layout calculations are complete
            so it can be relied upon to determine the DOM has loaded
        */
        const observer = new ResizeObserver(entries => {
            callback();
            observer.disconnect();
        });
        observer.observe(element);
    }

    handleRouteChange(evt) {
        this.triggerRouteChange(evt.target.closest('a').href);
        evt.preventDefault();
    }

    triggerRouteChange(url, stateChangeType = 'push') {
         try{
            // suppress error when page is being previewed inside Omni CMS.
            if(stateChangeType == 'push') {
                window.history.pushState(null, '', url);
                dispatchEvent(new PopStateEvent('popstate', null)); // Manually trigger popstate event
            } else {
                window.history.replaceState(null, '', url);
            }
        } catch(e){}
    }

    getRouteURL(route){
        const baseWidgetURL = vars.pubcalendar.basepath ? `${window.location.origin}${vars.pubcalendar.basepath}` : window.location.href.replace(window.location.hash, '');
        return `${baseWidgetURL}${vars.pubcalendar.basepath || route === '' ? '' : '#'}${route}`;
    }

    encode(str) {
        return encodeURIComponent(str).replaceAll('%2F', '%252F'); // forward slashes need to be double encoded;
    }

    decode(str) {
        return decodeURIComponent(str).replaceAll('%2F', '/'); // forward slashes needed to be double decoded
    }

    destroy() {
        this.eventController.abort();   // remove events listeners
        this.el.remove();
        delete this.el;
    }
}